import { Fragment } from 'react'
import { Box, CardMedia, Grid, Typography } from '@mui/material'
import { addDays, format } from 'date-fns'
import { useQuery } from 'react-query'

// local imports
import default_company_photo from 'assets/images/default_company_photo.png'
import { getCompanyDetails } from 'services/api/company-details'
import AdxDrawerComponent from 'components/reusable/drawer/AdxDrawerComponent'
import { renderSkills } from 'components/custom/AdxLabelRenderer'

const PreviewAdDialog = ({ open, handleClose, data, drawerState, handleDrawer, editJobAds }) => {
  const { data: companyDetails } = useQuery('companyDetails', getCompanyDetails)

  const { formData, position_level, job_specialization, qualification, grade, courses, skills } =
    data

  // job details data for component mapping
  let dateString = formData?.expired_date
  let date = new Date(dateString)
  let formattedDate = date?.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'numeric',
    year: 'numeric',
    separator: '/',
  })

  const JOB_DETAILS = [
    {
      id: 1,
      title: 'Position Level',
      description: position_level?.length ? position_level[0].level_name : '-',
    },
    {
      id: 2,
      title: 'Specialization',
      description: job_specialization?.length ? job_specialization[0].job_background_name : '-',
    },
    {
      id: 3,
      title: 'Employment Type',
      description: formData?.position_type !== '' ? formData.position_type : '-',
    },
    {
      id: 4,
      title: 'Salary',
      description:
        formData.min_salary && formData.max_salary
          ? `RM ${formData.min_salary} - RM ${formData.max_salary}`
          : '-',
    },
    {
      id: 5,
      title: 'Work Location',
      description: formData.city && formData.state ? `${formData.city}, ${formData.state}` : '-',
    },
  ]

  let experienceString
  if (formData.year_of_experience === 1) {
    experienceString = 'Fresh graduates are welcome to apply'
  } else if (formData.year_of_experience === 2) {
    experienceString = '1-3 years'
  } else if (formData.year_of_experience === 3) {
    experienceString = '3-5 years'
  } else if (formData.year_of_experience === 4) {
    experienceString = 'More than 5 years'
  } else {
    experienceString = '-'
  }
  // job requirement data for component map
  const JOB_REQUIREMENT = [
    {
      id: 1,
      title: 'Qualifications',
      description: qualification?.length ? qualification[0].qualification_name : '-',
    },
    {
      id: 3,
      title: 'Work Experience',
      description: experienceString,
    },

    // { id: 2, title: 'Grade', description: grade.length ? grade[0].grade : '-' },
    // {
    //   id: 3,
    //   title: 'Courses',
    //   description: courses?.length ? courses.map((e) => e.course).join(', ') : '-',
    // },
    // {
    //   id: 5,
    //   title: 'Skills',
    //   description: skills?.length ? skills.map((e) => e.name).join(', ') : '-',
    // },
  ]

  // skill card text tag component
  const Skills = ({ word }) => {
    return (
      <Box
        display="flex"
        alignItems="center"
        bgcolor="gray-background.main"
        sx={{
          borderRadius: '6px',
        }}
      >
        <Typography variant="body1" color="dark-text.main" padding={1} align="center">
          {word}
        </Typography>
      </Box>
    )
  }
  return (
    <AdxDrawerComponent
      state={drawerState}
      anchor="right"
      onClose={handleDrawer}
      sx={{ padding: 0, zIndex: 1 }}
      headerprops
      title="Preview Job Advertisement"
      noPaddingContent
    >
      {/* Job header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          p: 3,
          borderBottom: '1px solid',
          borderColor: 'gray-line.main',
        }}
      >
        <CardMedia
          component="img"
          sx={{
            height: 52,
            width: 52,
            objectFit: 'cover',
            borderRadius: '50%',
            // border: '3px solid',
            // borderColor: 'alternate.main',
          }}
          alt="Company Image"
          src={companyDetails?.data?.organization_logo || default_company_photo}
          loading="lazy"
        />
        <Box>
          <Typography variant="subtitle1">
            {formData.position_title ? formData.position_title : 'No position title'}
          </Typography>
          {companyDetails?.data?.organization_name ? (
            <Typography variant="subtitle1" color="light-gray-text.main">
              {companyDetails?.data?.organization_name}
            </Typography>
          ) : null}

          {formData?.job_advertisement_duration ? (
            <Typography variant="subtitle1" color="light-gray-text.main">
              {editJobAds
                ? `Expires on ${formattedDate}`
                : 'Expires on ' +
                  format(addDays(new Date(), formData.job_advertisement_duration), 'dd MMM yyyy')}
            </Typography>
          ) : null}
        </Box>
      </Box>
      <Box sx={{ px: 3, pb: 3 }}>
        {/* Job Details */}
        <Box mt={2}>
          <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
            Job Details
          </Typography>
          <Grid container gap={1} mt={1}>
            {JOB_DETAILS.map((e) => (
              <Fragment key={e.id}>
                <Grid item xs={12} sm={3}>
                  <Typography
                    variant="body1"
                    sx={{ color: 'light-gray-text.main', textTransform: 'capitalize' }}
                  >
                    {e.title}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={8}>
                  <Typography variant="body1" sx={{ textTransform: 'capitalize' }}>
                    {e.description ?? '-'}
                  </Typography>
                </Grid>
              </Fragment>
            ))}
          </Grid>
        </Box>
        {/* Job Description */}
        <Box mt={2}>
          <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
            Job Description
          </Typography>
          {formData?.job_description === '' ? (
            <Box mt={1} whiteSpace="pre-wrap">
              -
            </Box>
          ) : (
            <Box mt={1} whiteSpace="pre-wrap">
              {formData.job_description}
            </Box>
          )}
        </Box>
        {/* Job Requirement */}
        <Box mt={2}>
          <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
            Job Requirements
          </Typography>
          <Grid container gap={1} mt={1}>
            {JOB_REQUIREMENT?.map((e) => (
              <Fragment key={e.id}>
                <Grid item xs={12} sm={3}>
                  <Typography variant="body1" sx={{ color: 'light-gray-text.main' }}>
                    {e.title ?? '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={8}>
                  <Typography variant="body1">{e.description}</Typography>
                </Grid>
              </Fragment>
            ))}
          </Grid>
          <Box mt={1}>
            <Typography variant="body1" sx={{ color: 'light-gray-text.main' }}>
              Skills
            </Typography>
            {!Object.keys(skills)?.length ? (
              <Box whiteSpace="pre-wrap">-</Box>
            ) : (
              <Box
                sx={{
                  whiteSpace: 'pre-wrap',
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                {Object.keys(skills)?.map((item) => renderSkills(item))}
              </Box>
            )}
          </Box>
          <Box mt={1}>
            <Typography variant="body1" sx={{ color: 'light-gray-text.main' }}>
              Other Requirements
            </Typography>
            {formData?.job_requirement === '' ? (
              <Box whiteSpace="pre-wrap">-</Box>
            ) : (
              <Box whiteSpace="pre-wrap">{formData.job_requirement}</Box>
            )}
          </Box>
        </Box>
      </Box>
    </AdxDrawerComponent>
  )
}
export default PreviewAdDialog
